import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { MessageCircle, Coins, BellRing, CheckCircle2, Wallet, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIERS = [2000, 5000, 10000, 15000];

const WhatsAppAlerts = () => {
  const { vendor, refreshVendor } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingToggle, setSavingToggle] = useState(false);
  const [selected, setSelected] = useState(2000);
  const [custom, setCustom] = useState('');
  const [paying, setPaying] = useState(false);

  const token = () => localStorage.getItem('vendor_token');

  const fetchWallet = async () => {
    try {
      const res = await axios.get(`${API}/vendor/wallet`, { headers: { Authorization: `Bearer ${token()}` } });
      setWallet(res.data);
    } catch (e) {
      toast.error('Failed to load wallet');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchWallet(); }, []);

  const perInr = wallet?.rates?.sikka_per_inr || 10;
  const perMsg = wallet?.rates?.sikka_per_message || 5;
  const minTopup = wallet?.rates?.topup_min || 2000;

  // current chosen sikkas (tier or custom)
  const chosenSikkas = selected === 'custom' ? parseInt(custom || '0', 10) : selected;
  const chosenInr = chosenSikkas ? chosenSikkas / perInr : 0;
  const chosenMsgs = chosenSikkas ? Math.floor(chosenSikkas / perMsg) : 0;
  const validTopup = chosenSikkas >= minTopup && chosenSikkas % perInr === 0;

  const saveToggles = async (next) => {
    setSavingToggle(true);
    try {
      await axios.put(`${API}/vendor/wa-alerts`, next, { headers: { Authorization: `Bearer ${token()}` } });
      setWallet((w) => ({ ...w, wa_alerts: next }));
      toast.success('Alert settings saved');
    } catch (e) {
      toast.error('Could not save settings');
      fetchWallet();
    } finally { setSavingToggle(false); }
  };

  const toggle = (key, value) => {
    const next = { ...(wallet.wa_alerts || {}), [key]: value };
    saveToggles(next);
  };

  const loadRazorpay = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const handleTopup = async () => {
    if (!validTopup) { toast.error(`Minimum top-up is ${minTopup.toLocaleString()} Sikkas`); return; }
    setPaying(true);
    try {
      const orderRes = await axios.post(`${API}/vendor/wallet/create-topup`, { sikkas: chosenSikkas }, { headers: { Authorization: `Bearer ${token()}` } });
      const { order_id, amount, currency, key_id, sikkas } = orderRes.data;
      const ok = await loadRazorpay();
      if (!ok) { toast.error('Failed to load payment gateway'); setPaying(false); return; }
      const options = {
        key: key_id, amount, currency, name: 'SAANJH',
        description: `${sikkas.toLocaleString()} Sikka top-up`,
        order_id,
        handler: async (response) => {
          try {
            const v = await axios.post(
              `${API}/vendor/wallet/verify-topup?order_id=${response.razorpay_order_id}&payment_id=${response.razorpay_payment_id}&signature=${response.razorpay_signature}`,
              {}, { headers: { Authorization: `Bearer ${token()}` } }
            );
            toast.success(`${v.data.sikkas_added.toLocaleString()} Sikkas added!`);
            fetchWallet();
            if (refreshVendor) refreshVendor();
          } catch (e) {
            toast.error('Payment verification failed — contact support if charged');
          }
        },
        prefill: { name: vendor?.business_name || '', email: vendor?.email || '' },
        theme: { color: '#16a34a' },
        modal: { ondismiss: () => toast.info('Payment cancelled') },
      };
      new window.Razorpay(options).open();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start payment');
    } finally { setPaying(false); }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }

  const balance = wallet?.balance || 0;
  const msgsLeft = Math.floor(balance / perMsg);
  const wa = wallet?.wa_alerts || { new_order: false, order_confirmed: false, order_cancelled: false };
  const low = balance < perMsg;

  return (
    <div data-testid="whatsapp-alerts-page">
      <div className="mb-8 flex items-center gap-3">
        <div className="p-2 bg-green-100 rounded-lg"><MessageCircle className="h-6 w-6 text-green-600" /></div>
        <div>
          <h2 className="text-3xl font-bold tracking-tight">WhatsApp Alerts</h2>
          <p className="text-muted-foreground mt-1">Send WhatsApp notifications using your Sikka balance.</p>
        </div>
      </div>

      {/* Balance */}
      <Card className="mb-6 border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50">
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-400/20 rounded-xl"><Coins className="h-8 w-8 text-amber-600" /></div>
              <div>
                <p className="text-sm text-amber-700 font-medium">Sikka Balance</p>
                <p className="text-4xl font-extrabold text-amber-900">{balance.toLocaleString()} <span className="text-lg font-semibold">Sikkas</span></p>
                <p className="text-sm text-amber-700 mt-1">≈ <b>{msgsLeft.toLocaleString()}</b> messages left · {perMsg} Sikkas per message</p>
              </div>
            </div>
            <Badge variant={low ? 'destructive' : 'secondary'} className="text-sm">
              {low ? '⚠️ Top up to send alerts' : 'Active'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Toggles */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Alert types</CardTitle>
          <CardDescription>Turn on the messages you want. Each message sent costs {perMsg} Sikkas. When your balance runs out, messages pause automatically.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div className="flex items-start gap-3">
              <BellRing className="h-5 w-5 text-purple-600 mt-0.5" />
              <div>
                <p className="font-semibold">Store Alert for new order</p>
                <p className="text-sm text-muted-foreground">A WhatsApp message to your store number the moment a new order arrives.</p>
              </div>
            </div>
            <Switch checked={!!wa.new_order} disabled={savingToggle} onCheckedChange={(v) => toggle('new_order', v)} data-testid="toggle-new-order" />
          </div>
          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-semibold">Order Confirmation</p>
                <p className="text-sm text-muted-foreground">A WhatsApp confirmation to the customer when you confirm their order.</p>
              </div>
            </div>
            <Switch checked={!!wa.order_confirmed} disabled={savingToggle} onCheckedChange={(v) => toggle('order_confirmed', v)} data-testid="toggle-order-confirmed" />
          </div>
          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div className="flex items-start gap-3">
              <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
              <div>
                <p className="font-semibold">Order Cancellation</p>
                <p className="text-sm text-muted-foreground">A WhatsApp notification to the customer when you cancel their order.</p>
              </div>
            </div>
            <Switch checked={!!wa.order_cancelled} disabled={savingToggle} onCheckedChange={(v) => toggle('order_cancelled', v)} data-testid="toggle-order-cancelled" />
          </div>
          {!vendor?.whatsapp && wa.new_order && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
              Add your store WhatsApp number in <b>Manage Store → Account</b> so new-order alerts can be delivered.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Top up */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2"><Wallet className="h-5 w-5" /><CardTitle>Top up Sikkas</CardTitle></div>
          <CardDescription>1 INR = {perInr} Sikkas. Minimum top-up {minTopup.toLocaleString()} Sikkas. Paid securely via Razorpay.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
            {TIERS.map((t) => (
              <button key={t} onClick={() => setSelected(t)}
                className={`rounded-xl border-2 p-4 text-center transition-all ${selected === t ? 'border-green-600 bg-green-50' : 'border-muted hover:border-green-300'}`}
                data-testid={`tier-${t}`}>
                <p className="text-lg font-bold">{t.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Sikkas</p>
                <p className="text-sm font-medium text-green-700 mt-1">₹{(t / perInr).toLocaleString()}</p>
              </button>
            ))}
            <button onClick={() => setSelected('custom')}
              className={`rounded-xl border-2 p-4 text-center transition-all ${selected === 'custom' ? 'border-green-600 bg-green-50' : 'border-muted hover:border-green-300'}`}
              data-testid="tier-custom">
              <p className="text-lg font-bold">Custom</p>
              <p className="text-xs text-muted-foreground">your amount</p>
            </button>
          </div>

          {selected === 'custom' && (
            <div className="mb-5 max-w-xs">
              <label className="text-sm font-medium">Sikkas (min {minTopup.toLocaleString()}, multiples of {perInr})</label>
              <Input type="number" min={minTopup} step={perInr} value={custom} onChange={(e) => setCustom(e.target.value)} placeholder={`${minTopup}`} className="mt-1" data-testid="custom-sikkas" />
            </div>
          )}

          {/* Price calculator */}
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl bg-muted/50 p-4 mb-5">
            <div className="text-sm">
              <p className="text-muted-foreground">You get</p>
              <p className="text-2xl font-bold">{(chosenSikkas || 0).toLocaleString()} Sikkas <span className="text-sm font-normal text-muted-foreground">≈ {chosenMsgs.toLocaleString()} messages</span></p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">You pay</p>
              <p className="text-3xl font-extrabold text-green-700">₹{(chosenInr || 0).toLocaleString()}</p>
            </div>
          </div>

          <Button onClick={handleTopup} disabled={!validTopup || paying} className="w-full bg-green-600 hover:bg-green-700 h-12 text-base" data-testid="pay-btn">
            {paying ? 'Starting payment…' : (validTopup ? `Pay ₹${chosenInr.toLocaleString()} — add ${chosenSikkas.toLocaleString()} Sikkas` : `Minimum ${minTopup.toLocaleString()} Sikkas`)}
          </Button>
        </CardContent>
      </Card>

      {/* Ledger */}
      {wallet?.ledger?.length > 0 && (
        <Card className="mt-6">
          <CardHeader><CardTitle className="text-base">Recent activity</CardTitle></CardHeader>
          <CardContent>
            <div className="divide-y text-sm">
              {wallet.ledger.slice(0, 12).map((l, i) => (
                <div key={i} className="flex items-center justify-between py-2">
                  <div>
                    <span className="font-medium capitalize">{l.type === 'debit' ? (l.reason === 'new_order' ? 'New-order alert' : l.reason === 'order_cancelled' ? 'Order cancellation' : 'Order confirmation') : l.type === 'credit' ? 'Top-up' : l.type}</span>
                    {l.note && <span className="text-muted-foreground"> · {l.note}</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={l.amount > 0 ? 'text-green-600 font-semibold' : 'text-muted-foreground'}>{l.amount > 0 ? '+' : ''}{l.amount} Sikkas</span>
                    <span className="text-xs text-muted-foreground">{l.created_at ? new Date(l.created_at).toLocaleString() : ''}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WhatsAppAlerts;
